import { EmptyView, emptyViewVariants } from '@components'

import { ModuleLayout } from '@billy/design-system'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router'

import { FileListSkeleton } from '@views/documents/elements/FileList'

import { useOrganizationViewUrl } from '../../../../hooks/routing/useOrganizationViewUrl'
import { reactRoute } from '../../../../utils/routing/reactRoute'
import { useDmsUserRole } from '../../hooks/useDmsUserRole'
import { useFetchFolderTree } from '../../hooks/useFetchFolderTree'
import { useIsClientDocumentsRoute } from '../../hooks/useIsClientDocumentsRoute'
import { getDocumentsRoutes } from '../../utils/getDocumentsRoutes'
import { slugifyFolderName } from '../../utils/slugifyFolderName'

export const DocumentsEmptyState = () => {
  const { t } = useTranslation()
  const { animation, description, title } = emptyViewVariants.documents
  const { isLoading: isRoleLoading, isBoWithCpa, isCpaClientMode, isCpaExpertMode } = useDmsUserRole()
  const { tree, isLoading: isFolderTreeLoading } = useFetchFolderTree()
  const isClientDocumentsRoute = useIsClientDocumentsRoute()
  const documentsRoutes = getDocumentsRoutes(isClientDocumentsRoute)

  const firstFolder = tree?.find((n) => !n.parentId)
  const firstFolderId = firstFolder?.id
  const firstFolderName = firstFolder?.name
  const firstFolderSlug = firstFolderName ? slugifyFolderName(firstFolderName) : undefined
  const firstFolderRoute = firstFolderId ? documentsRoutes.getFolderRoute(firstFolderId, firstFolderSlug) : ''
  const { url: firstFolderUrl } = useOrganizationViewUrl(firstFolderRoute)
  const { url: unsortedUrl } = useOrganizationViewUrl(reactRoute.organization.documents.getUnsortedRoute())
  const { url: sharedByMeUrl } = useOrganizationViewUrl(reactRoute.organization.clientDocuments.getSharedByMeRoute())

  const isLoading = isRoleLoading || isFolderTreeLoading

  if (isLoading) {
    return (
      <ModuleLayout fullWidth title={t('navigation.menu.label.documents')} withMenuOffset={false}>
        <FileListSkeleton />
      </ModuleLayout>
    )
  }

  if (isCpaExpertMode && unsortedUrl) {
    return <Navigate to={unsortedUrl} replace />
  }

  if ((isBoWithCpa || isCpaClientMode) && sharedByMeUrl) {
    return <Navigate to={sharedByMeUrl} replace />
  }

  // firstFolderRoute always builds an expert-namespaced URL (folder browsing has no client-mode
  // equivalent route), and the advisor layer mode is resolved from the URL path segment - so an
  // advisor in Client mode landing here must be redirected away above, or this would silently flip
  // them into Expert mode.
  if (firstFolderId && firstFolderUrl) {
    return <Navigate to={firstFolderUrl} replace />
  }

  return (
    <ModuleLayout fullWidth title={t('navigation.menu.label.documents')} withMenuOffset={false}>
      <div data-testid="documents">
        <EmptyView animationName={animation} tDescription={description} tTitle={title} />
      </div>
    </ModuleLayout>
  )
}
